/**
 * Отправка отчётов смены с телефона без открытого черновика (ПМ-09, ПР-04): все отчёты владельца на этом телефоне —
 * фото, уже поставленные команды и черновики с автоотправкой — уходят сами, когда появилась связь. Экран отчёта и этот
 * цикл берут одну блокировку на отчёт: одновременно над отчётом работает только один из них.
 */
import type { SitePhotoFileLookup, SiteReportDraft } from '@epc/contracts';
import { ApiError, api } from '../../../lib/api';
import { pendingCommand, SiteEditor } from '../editor';
import type { DayPackage, DayPlan } from '../model';
import { siteTransport } from '../transport';
import { type PhotoUploader, photosPending } from './photo-sync';
import type { SiteTransport } from './queue';
import type { LocalSiteReport, OfflineScope, SiteOfflineStore } from './store';

/** Область очереди на этом телефоне: компания, человек и постоянный номер устройства */
export function siteDeviceScope(me: { user: { id: string }; tenant: { id: string } | null }): OfflineScope {
  let deviceId = localStorage.getItem('epc:site:device');
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    localStorage.setItem('epc:site:device', deviceId);
  }
  return { tenantId: me.tenant!.id, userId: me.user.id, deviceId };
}

/**
 * Загрузка фото в общий файловый слой портала от имени владельца очереди; антивирус проверяет файл на сервере (DEV-10).
 * Запросы несут ожидаемую личность: если на телефоне вошёл другой человек, сервер откажет, и фото останется на телефоне.
 */
export function sitePhotoUploaderFor(scope: Pick<OfflineScope, 'tenantId' | 'userId'>): PhotoUploader {
  const expectedIdentity = { tenantId: scope.tenantId, userId: scope.userId };
  return {
    async upload(blob, fileName) {
      const form = new FormData();
      form.append('kind', 'site_photo');
      form.append('file', blob, fileName);
      return api<{ id: string }>('/files', { form, expectedIdentity });
    },
    async findExisting(sha256, size) {
      try {
        const found = await api<SitePhotoFileLookup>('/site/photo-files/lookup', {
          query: { sha256, size },
          expectedIdentity,
        });
        return found.fileId;
      } catch (e) {
        // Сервер ещё без поиска (идёт выкладка) — просто загружаем; сеть и вход решает общий разбор ошибок
        if (e instanceof ApiError && (e.status === 404 || e.status === 405)) return null;
        throw e;
      }
    },
  };
}

/** Блокировка отчёта: экран и фоновый цикл не работают над одним отчётом одновременно */
export const siteReportLock = (scope: OfflineScope, clientId: string) =>
  `epc-site-report:${scope.tenantId}:${scope.userId}:${scope.deviceId}:${clientId}`;

/** Взять блокировку, если свободна; занята — пропустить (её держит экран или другая вкладка) */
export async function withSiteReportLock<T>(name: string, fn: () => Promise<T>): Promise<T | null> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks) return fn();
  return locks.request(name, { ifAvailable: true }, async (lock) => (lock ? fn() : null));
}

/** Есть ли у отчёта на телефоне то, что должно уйти на сервер */
export function siteReportWaiting(row: LocalSiteReport): boolean {
  const open = pendingCommand(row);
  // Отказ или встречная правка ждут решения человека, а не связи
  if (open && open.state !== 'pending') return false;
  if (open) return true;
  if (photosPending(row.draft as SiteReportDraft, row.photos) > 0) return true;
  return row.autoSend === true;
}

/** Версия графика пакета для новой команды — из плана дня (запоминается для работы без связи) */
export async function siteReportSources(
  row: LocalSiteReport,
  scope?: Pick<OfflineScope, 'tenantId' | 'userId'>,
): Promise<DayPackage['sourceRevisions'] | null> {
  const draft = row.draft as SiteReportDraft;
  try {
    const plan = await api<DayPlan>(`/site/projects/${draft.projectId}/day-plan`, {
      query: { date: draft.reportDay },
      ...(scope ? { expectedIdentity: { tenantId: scope.tenantId, userId: scope.userId } } : {}),
    });
    return plan.packages.find((p) => p.packageId === draft.packageId)?.sourceRevisions ?? null;
  } catch {
    return null;
  }
}

export type SiteReportsSyncResult = { advanced: number; waiting: number; paused: number };

/**
 * Пауза по отчёту после отказа, как на экране отчёта: сервер не принял фото — 5 минут, вход или чужая сессия — 30 секунд,
 * план дня не читается — 5 минут. Иначе тот же файл уходил бы по мобильной связи каждые 30 секунд.
 */
const holds = new Map<string, number>();
const HOLD_REFUSED = 5 * 60_000;
const HOLD_AUTH = 30_000;

/**
 * Один проход по всем отчётам владельца на телефоне. Отчёт с паузой входа, конфликтом или отказом не трогается:
 * дальше решает человек на экране отчёта.
 */
/** Сбросить паузы (тесты, смена человека на телефоне) */
export function resetSiteReportHolds() {
  holds.clear();
}

export async function syncLocalSiteReports(
  store: SiteOfflineStore,
  scope: OfflineScope,
  options: {
    isCurrentIdentity: () => boolean;
    loadSources?: (row: LocalSiteReport) => Promise<DayPackage['sourceRevisions'] | null>;
    uploader?: PhotoUploader;
    transport?: SiteTransport;
    now?: () => number;
  },
): Promise<SiteReportsSyncResult> {
  const loadSources = options.loadSources ?? ((row: LocalSiteReport) => siteReportSources(row, scope));
  const uploader = options.uploader ?? sitePhotoUploaderFor(scope);
  const now = options.now ?? Date.now;
  let advanced = 0;
  for (const row of await store.list(scope)) {
    if (!options.isCurrentIdentity()) break;
    if (row.authPaused || !siteReportWaiting(row)) continue;
    const open = pendingCommand(row);
    if (open && open.state !== 'pending') continue;
    const lock = siteReportLock(scope, row.clientId);
    if ((holds.get(lock) ?? 0) > now()) continue;
    const hold = (stopped: string | null | undefined, missingPlan = false) => {
      if (stopped === 'refused' || missingPlan) holds.set(lock, now() + HOLD_REFUSED);
      else if (stopped === 'auth') holds.set(lock, now() + HOLD_AUTH);
      else holds.delete(lock);
    };
    const done = await withSiteReportLock(lock, async () => {
      const editor = new SiteEditor(store, scope, row.clientId, options.transport ?? siteTransport(scope));
      const current = await editor.load();
      if (!current || current.authPaused || !siteReportWaiting(current)) return false;
      // Без автоотправки — только фото и уже поставленная команда
      if (!current.autoSend) {
        const photos = await editor.syncPhotos(uploader);
        hold(photos.stopped);
        if (
          photos.stopped !== 'refused' &&
          photos.stopped !== 'auth' &&
          pendingCommand(photos.row)?.state === 'pending'
        )
          await editor.sync();
        return true;
      }
      // Новая команда (сохранить или отправить на проверку) требует версию графика; без неё — только фото
      const needsCommand = current.autoSend && !pendingCommand(current);
      const sources = needsCommand ? await loadSources(current) : [];
      if (!sources) {
        const photos = await editor.syncPhotos(uploader);
        hold(photos.stopped, true);
        return true;
      }
      const r = await editor.advance(sources, uploader);
      hold(r.photos.stopped);
      return true;
    });
    if (done) advanced += 1;
  }
  const rows = await store.list(scope);
  return {
    advanced,
    waiting: rows.filter((r) => !r.authPaused && siteReportWaiting(r)).length,
    paused: rows.filter((r) => r.authPaused).length,
  };
}
